import React from 'react';
import { CleroButtonWordmark } from './Logo';
import CompareShell, { AppStoreBadges, InstitutionalTrustBar } from './compare/CompareShell';

type Props = {
  token: string;
};

const DirectPayClaimPage: React.FC<Props> = ({ token }) => {
  const trimmedToken = String(token || '').trim();
  const deepLink = trimmedToken ? `clero://claim/${encodeURIComponent(trimmedToken)}` : 'clero://';
  const query = new URLSearchParams(window.location.search || '');
  const senderName = String(query.get('sender') || '').trim() || 'Someone';
  const amountRaw = Number(query.get('amount') || 0);
  const hasAmount = Number.isFinite(amountRaw) && amountRaw > 0;
  const amountFormatted = hasAmount ? `$${amountRaw.toFixed(2)}` : null;

  const handleOpenApp = () => {
    window.location.href = deepLink;
  };

  return (
    <CompareShell>
      {/* HERO SECTION WITH ATMOSPHERIC AMBIENT MESH */}
      <section className="relative overflow-hidden pt-8 sm:pt-14 pb-14 sm:pb-20 px-4 sm:px-6">
        {/* Soft Ambient Radial Lights (Faithful to Landing Page public-hero-mesh) */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[500px] w-full max-w-5xl rounded-full bg-[radial-gradient(circle_at_50%_20%,rgba(13,148,136,0.12),rgba(98,199,255,0.06)_40%,transparent_70%)] blur-3xl -z-10"
        />

        <div className="relative mx-auto max-w-2xl rounded-[32px] sm:rounded-[40px] bg-white/95 backdrop-blur-2xl p-8 sm:p-12 shadow-[0_28px_80px_-16px_rgba(15,23,42,0.10),0_1px_2px_rgba(15,23,42,0.04)] border border-slate-200/80 ring-1 ring-white/60 text-center space-y-6">
          {/* Amount Display */}
          {amountFormatted ? (
            <div className="space-y-1">
              <div className="text-4xl sm:text-6xl font-black text-slate-950 tracking-tight">
                {amountFormatted}
              </div>
              <div className="text-sm sm:text-base font-bold text-teal-700">
                sent from {senderName}
              </div>
            </div>
          ) : (
            <img
              src="/clero_icon.png"
              alt="Clero"
              className="mx-auto h-20 w-20 rounded-2xl shadow-md ring-4 ring-slate-100/90 object-cover"
            />
          )}

          {/* Heading & Subtitle */}
          <div className="space-y-3">
            <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-slate-950 leading-tight">
              {amountFormatted
                ? `${senderName} sent you money on Clero.`
                : `${senderName} sent you a payment on Clero.`}
            </h1>
            <p className="mx-auto max-w-lg text-base sm:text-lg text-slate-600 font-medium leading-relaxed">
              Open the app to claim your funds directly to your checking account with same-day deposit* and $0 fees.
            </p>
          </div>

          {/* Primary Action Button (Clean, no overlapping arrow icon) */}
          <div className="pt-2">
            <button
              type="button"
              onClick={handleOpenApp}
              className="inline-flex items-center justify-center rounded-2xl bg-[var(--clero-primary)] hover:bg-[var(--clero-primary-dark)] text-white font-extrabold text-base sm:text-lg px-8 py-4 shadow-[0_10px_25px_-5px_rgba(13,148,136,0.38)] hover:shadow-[0_16px_32px_-6px_rgba(13,148,136,0.48)] hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] transition-all duration-200 w-full sm:w-auto cursor-pointer"
            >
              <span className="whitespace-nowrap flex items-center gap-2">
                <span>Open in</span>
                <CleroButtonWordmark height={17} color="#FFFFFF" className="inline-block shrink-0" />
              </span>
            </button>
          </div>

          {/* Download App Badges */}
          <div className="pt-2 flex items-center justify-center">
            <AppStoreBadges badgeHeightClass="h-10 sm:h-11" />
          </div>
        </div>
      </section>

      {/* INSTITUTIONAL BANKING INFRASTRUCTURE & SECURITY METRICS BAR */}
      <InstitutionalTrustBar />
    </CompareShell>
  );
};

export default DirectPayClaimPage;
